import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { Account, LoginTotp } from '../models/account.model';
import { ERROR_TYPE } from '../models/api.model';
import { errorTypeOf } from '../api/api-error';
import { retryAfterText } from '../../shared/ui/retry-after';

export type AuthStatus = 'unknown' | 'ready' | 'anonymous' | 'expired' | 'error';

/**
 * Esito di un passaggio del login.
 *
 * `totp` significa password giusta ma codice ancora dovuto; `challenge-expired` che il login
 * in sospeso è scaduto e si riparte dalla password; `too-many` porta già il "tra quanto".
 */
export type LoginOutcome =
  | { kind: 'ready' }
  | { kind: 'totp'; totp: LoginTotp }
  | { kind: 'invalid-credentials' }
  | { kind: 'invalid-code' }
  | { kind: 'challenge-expired' }
  | { kind: 'too-many'; retryAfter: string }
  | { kind: 'error' };

/** POST /api/admin/auth/login e /login/totp (routes/AdminRoute.kt). */
interface AdminLoginResponse {
  account: Account | null;
  totp: LoginTotp | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);

  private readonly state = signal<AuthStatus>('unknown');
  private readonly account = signal<Account | null>(null);
  private probing: Promise<AuthStatus> | null = null;

  readonly status = this.state.asReadonly();
  readonly currentAccount = this.account.asReadonly();
  readonly isAuthenticated = computed(() => this.state() === 'ready');

  readonly displayName = computed(() => {
    const account = this.account();
    if (account === null) {
      return null;
    }
    const name = [account.firstName, account.lastName].filter((part) => !!part).join(' ');
    return name !== '' ? name : account.username;
  });

  ensureProbed(): Promise<AuthStatus> {
    if (this.state() !== 'unknown') {
      return Promise.resolve(this.state());
    }
    this.probing ??= this.probe().finally(() => {
      this.probing = null;
    });
    return this.probing;
  }

  reprobe(): Promise<AuthStatus> {
    this.state.set('unknown');
    return this.ensureProbed();
  }

  private async probe(): Promise<AuthStatus> {
    try {
      const account = await firstValueFrom(this.http.get<Account>('/api/admin/auth/me'));
      this.account.set(account);
      this.state.set('ready');
      return 'ready';
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.reset('anonymous');
        return 'anonymous';
      }
      this.reset('error');
      return 'error';
    }
  }

  login(username: string, password: string): Promise<LoginOutcome> {
    return this.attempt(
      this.http.post<AdminLoginResponse>('/api/admin/auth/login', { username, password }),
    );
  }

  /** Secondo passaggio: il codice dell'app di autenticazione. */
  loginTotp(challenge: string, code: string): Promise<LoginOutcome> {
    return this.attempt(
      this.http.post<AdminLoginResponse>('/api/admin/auth/login/totp', { challenge, code }),
    );
  }

  private async attempt(request: Observable<AdminLoginResponse>): Promise<LoginOutcome> {
    try {
      const response = await firstValueFrom(request);
      if (response.totp) {
        return { kind: 'totp', totp: response.totp };
      }
      if (response.account === null) {
        throw new Error('Login senza account né verifica');
      }
      this.account.set(response.account);
      this.state.set('ready');
      return { kind: 'ready' };
    } catch (error) {
      this.reset('anonymous');
      if (error instanceof HttpErrorResponse) {
        const type = errorTypeOf(error);
        if (error.status === 429 || type === ERROR_TYPE.tooManyRequests) {
          return { kind: 'too-many', retryAfter: retryAfterText(error) };
        }
        // Prima i tipi del secondo passaggio, poi lo status: un codice sbagliato può uscire
        // anch'esso come 401, e non va scambiato per una password sbagliata.
        if (type === ERROR_TYPE.totpInvalidCode) {
          return { kind: 'invalid-code' };
        }
        if (type === ERROR_TYPE.totpInvalidChallenge) {
          return { kind: 'challenge-expired' };
        }
        if (error.status === 401) {
          return { kind: 'invalid-credentials' };
        }
      }
      this.state.set('error');
      return { kind: 'error' };
    }
  }

  private reset(status: AuthStatus): void {
    this.account.set(null);
    this.state.set(status);
  }

  markExpired(): void {
    this.reset('expired');
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.http.post<void>('/api/admin/auth/logout', null));
    } catch {}
    this.reset('anonymous');
  }
}
